/**
 * feat-300 — the proposal: summary, field-by-field diff, and its actions
 * (design NOTES §3.6, §3.7; ADR §10.6).
 *
 * - Rows are ONLY the keys of `proposal.changes`, answer-key fields first.
 * - The Current side is `proposal.base[field]`, never today's live row (NOTES §3.6).
 * - "No change needed" has no diff and no Apply.
 * - Apply always goes through an inline confirm whose sentence (and button)
 *   branches on `approval_required` (A7).
 * - A known-stale proposal (`is_stale`, or a 409 `stale_base`) offers NO Apply:
 *   the stale box with "Ask AI to redo" and Discard replaces the actions.
 *
 * UNTRUSTED: summary and values are text nodes.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import { AlertTriangle, Check, RotateCcw } from 'lucide-react';
import { QuizQuestion } from '../../../../services/quizContentAdminService';
import {
  EditableQuestionField,
  QuestionEditProposal,
  QuestionEditThread,
} from '../../../../services/questionEditService';
import { QUESTION_FIELD_LABELS } from '../../curriculum/QuestionDetailForm';
import { ANSWER_KEY_FIELDS, sortFieldsAnswerKeyFirst } from '../../curriculum/approvalSelection';
import { APPROVAL_QUEUE_COPY } from '../../curriculum/approvalCopy';
import { SaveSpinner } from '../../curriculum/SaveStatus';
import {
  ANSWER_KEY_TAG,
  BOX_CONFIRM,
  BOX_STALE,
  BTN_PRIMARY,
  BTN_QUIET,
  BTN_SECONDARY,
  CUR_VAL,
  DIFF,
  DIFF_GRID,
  DIFF_LABEL,
  DIFF_ROW,
  EYEBROW,
  HINT,
  PROP_ACTIONS,
  PROP_VAL,
  PROPOSAL,
  PROPOSAL_HEAD,
  SIDE,
  SUMMARY,
  VARIANT_ADDED,
  VARIANT_CHIP,
  VARIANT_KEPT,
  VARIANT_REMOVED,
} from './classes';
import { QUESTION_EDIT_COPY as COPY } from './voiceEditCopy';

export type ApplyOutcome = 'applied' | 'stale' | 'error';

export interface ProposalActions {
  onApply: () => Promise<ApplyOutcome>;
  onReply: () => void;
  onDiscard: () => void;
  onRedo: () => void;
  busy: boolean;
}

/** Display order inside each rank (sortFieldsAnswerKeyFirst keeps it stable). */
const FIELD_ORDER: EditableQuestionField[] = [
  'correct_option',
  'blank_accepted_variants',
  'question_text',
  'option_a',
  'option_b',
  'option_c',
  'option_d',
  'explanation',
  'correct_explanation',
];

export function orderedChangedFields(changes: QuestionEditProposal['changes']): EditableQuestionField[] {
  const keys = FIELD_ORDER.filter((f) => Object.prototype.hasOwnProperty.call(changes, f));
  return sortFieldsAnswerKeyFirst(keys.map((field) => ({ field }))).map((r) => r.field);
}

const Empty: React.FC = () => (
  <em className="text-inkMuted">{APPROVAL_QUEUE_COPY.emptyApprovedValue}</em>
);

const splitVariants = (v: string | null | undefined): string[] =>
  (v ?? '')
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);

/** `B) “…”` — the letter AND that option's text, so a moved ✅ never reads as a
 *  one-character diff (NOTES §3.6). */
function answerText(
  letter: string | null | undefined,
  side: 'current' | 'proposed',
  proposal: QuestionEditProposal,
  q: QuizQuestion | null,
): React.ReactNode {
  if (!letter) return <Empty />;
  const field = `option_${letter}` as EditableQuestionField;
  let text: string | null | undefined;
  if (side === 'proposed' && Object.prototype.hasOwnProperty.call(proposal.changes, field)) {
    text = proposal.changes[field];
  } else if (side === 'current' && Object.prototype.hasOwnProperty.call(proposal.base, field)) {
    text = proposal.base[field];
  } else if (q) {
    text = q[field as 'option_a'];
  }
  return (
    <>
      {letter.toUpperCase()}){text ? ` “${text}”` : ''}
    </>
  );
}

const VariantsCurrent: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const items = splitVariants(value);
  if (!items.length) return <Empty />;
  return (
    <div>
      {items.map((v) => (
        <span key={v} className={`${VARIANT_CHIP} ${VARIANT_KEPT}`}>
          {v}
        </span>
      ))}
    </div>
  );
};

const VariantsProposed: React.FC<{ base: string | null | undefined; next: string | null | undefined }> = ({
  base,
  next,
}) => {
  const before = splitVariants(base);
  const after = splitVariants(next);
  const beforeSet = new Set(before);
  const afterSet = new Set(after);
  const removed = before.filter((v) => !afterSet.has(v));
  if (!after.length && !removed.length) return <Empty />;
  return (
    <div>
      {after.map((v) =>
        beforeSet.has(v) ? (
          <span key={`k-${v}`} className={`${VARIANT_CHIP} ${VARIANT_KEPT}`}>
            {v}
          </span>
        ) : (
          <span key={`a-${v}`} className={`${VARIANT_CHIP} ${VARIANT_ADDED}`} data-variant="added">
            <span aria-hidden>+ </span>
            <span className="sr-only">{COPY.addedSr}</span>
            {v}
          </span>
        ),
      )}
      {removed.map((v) => (
        <span key={`r-${v}`} className={`${VARIANT_CHIP} ${VARIANT_REMOVED}`} data-variant="removed">
          <span className="sr-only">{COPY.removedSr}</span>
          {v}
        </span>
      ))}
    </div>
  );
};

export const ProposalDiff: React.FC<{
  proposal: QuestionEditProposal;
  q: QuizQuestion | null;
  dim?: boolean;
}> = ({ proposal, q, dim = false }) => {
  const fields = orderedChangedFields(proposal.changes);
  return (
    <dl className={`${DIFF}${dim ? ' opacity-60' : ''}`}>
      {fields.map((f) => {
        const cur = proposal.base[f];
        const next = proposal.changes[f];
        return (
          <div key={f} className={DIFF_ROW} data-testid={`qe-diff-${f}`}>
            <dt className={DIFF_LABEL}>
              {QUESTION_FIELD_LABELS[f]}
              {ANSWER_KEY_FIELDS.has(f) && (
                <span className={ANSWER_KEY_TAG}>{APPROVAL_QUEUE_COPY.answerKeyTag}</span>
              )}
            </dt>
            <dd className={DIFF_GRID}>
              <span className={SIDE}>{COPY.current}</span>
              {/* Security M3: the diff values are AI/reviewer-derived text. */}
              <span data-sentry-mask className={CUR_VAL} data-side="current">
                {f === 'correct_option' ? (
                  answerText(cur, 'current', proposal, q)
                ) : f === 'blank_accepted_variants' ? (
                  <VariantsCurrent value={cur} />
                ) : cur ? (
                  cur
                ) : (
                  <Empty />
                )}
              </span>
              <span className={SIDE}>{COPY.proposed}</span>
              <span data-sentry-mask className={PROP_VAL} data-side="proposed">
                {f === 'correct_option' ? (
                  answerText(next, 'proposed', proposal, q)
                ) : f === 'blank_accepted_variants' ? (
                  <VariantsProposed base={cur} next={next} />
                ) : next ? (
                  next
                ) : (
                  <Empty />
                )}
              </span>
            </dd>
          </div>
        );
      })}
    </dl>
  );
};

type Mode = 'actions' | 'confirm' | 'applying';

const ProposalCard: React.FC<{
  thread: QuestionEditThread;
  proposal: QuestionEditProposal;
  q: QuizQuestion | null;
  approvalRequired: boolean | null;
  actions: ProposalActions;
}> = ({ thread, proposal, q, approvalRequired, actions }) => {
  const [mode, setMode] = useState<Mode>('actions');
  /** A 409 stale_base whose envelope carried no thread. */
  const [staleLocal, setStaleLocal] = useState(false);
  const stale = thread.is_stale || staleLocal;
  const confirmRef = useRef<HTMLDivElement | null>(null);
  const staleRef = useRef<HTMLDivElement | null>(null);
  const applyRef = useRef<HTMLButtonElement | null>(null);
  const sentenceId = `qe-confirm-${useId()}`;
  const n = Object.keys(proposal.changes).length;

  useEffect(() => {
    if (mode === 'confirm') confirmRef.current?.focus();
  }, [mode]);

  const confirm = async () => {
    setMode('applying');
    const outcome = await actions.onApply();
    if (outcome === 'stale') {
      // The panel moves focus to the stale box (it survives a re-mount).
      setStaleLocal(true);
      setMode('actions');
    } else if (outcome === 'error') {
      setMode('confirm');
    }
    // 'applied': the thread closes and the panel swaps this card for the box.
  };

  const confirmSentence =
    approvalRequired === true
      ? COPY.applyConfirmRequired
      : approvalRequired === false
        ? COPY.applyConfirmOff
        : COPY.applyConfirmUnknown;
  const confirmButton =
    approvalRequired === false ? COPY.applyConfirmBtnOff : COPY.applyConfirmBtnRequired;

  if (proposal.no_change) {
    return (
      <div className={PROPOSAL} data-testid="qe-proposal" data-no-change="true">
        <div className={PROPOSAL_HEAD}>
          <p className={EYEBROW}>{COPY.noChangeEyebrow}</p>
          <p data-sentry-mask className={`mt-0.5 ${SUMMARY}`} data-testid="qe-summary">
          {proposal.summary}
        </p>
        </div>
        <div className={PROP_ACTIONS}>
          <button type="button" className={BTN_SECONDARY} onClick={actions.onReply}>
            {COPY.reply}
          </button>
          <button type="button" className={BTN_QUIET} onClick={actions.onDiscard} disabled={actions.busy}>
            {COPY.closeThread}
          </button>
          <span className={`${HINT} ml-auto`}>{COPY.nothingToApply}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={PROPOSAL} data-testid="qe-proposal">
      <div className={PROPOSAL_HEAD}>
        <p className={EYEBROW}>{COPY.proposalEyebrow(n)}</p>
        <p data-sentry-mask className={`mt-0.5 ${SUMMARY}`} data-testid="qe-summary">
          {proposal.summary}
        </p>
      </div>
      <ProposalDiff proposal={proposal} q={q} dim={stale} />

      {stale ? (
        <div className="border-t border-softBorder px-3 py-2.5">
          <div
            ref={staleRef}
            className={BOX_STALE}
            role="alert"
            tabIndex={-1}
            data-testid="qe-stale"
            data-qe-stale=""
          >
            <p className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden />
              <span>
                <strong className="font-semibold">{COPY.staleLead}</strong>
                {COPY.staleTail}
              </span>
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <button type="button" className={BTN_PRIMARY} onClick={actions.onRedo} disabled={actions.busy}>
                <RotateCcw className="w-3.5 h-3.5" aria-hidden />
                {COPY.askRedo}
              </button>
              <button
                type="button"
                className={`${BTN_QUIET} !text-attention-on hover:!bg-white/60`}
                onClick={actions.onDiscard}
                disabled={actions.busy}
              >
                {COPY.discard}
              </button>
            </div>
          </div>
        </div>
      ) : mode === 'actions' ? (
        <div className={PROP_ACTIONS}>
          <button
            ref={applyRef}
            type="button"
            className={BTN_PRIMARY}
            onClick={() => setMode('confirm')}
            disabled={actions.busy}
          >
            <Check className="w-3.5 h-3.5" aria-hidden />
            {COPY.apply}
          </button>
          <button type="button" className={BTN_SECONDARY} onClick={actions.onReply}>
            {COPY.reply}
          </button>
          <button type="button" className={BTN_QUIET} onClick={actions.onDiscard} disabled={actions.busy}>
            {COPY.discard}
          </button>
          <span className={`${HINT} ml-auto hidden sm:inline`}>{COPY.refineHint}</span>
        </div>
      ) : (
        <div className="border-t border-softBorder px-3 py-2.5">
          <div
            ref={confirmRef}
            className={BOX_CONFIRM}
            role="group"
            aria-labelledby={sentenceId}
            tabIndex={-1}
            data-testid="qe-confirm"
          >
            <p id={sentenceId}>
              <strong className="font-semibold">{COPY.applyConfirmLead}</strong> {confirmSentence}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <button
                type="button"
                className={BTN_PRIMARY}
                onClick={confirm}
                disabled={mode === 'applying'}
                aria-busy={mode === 'applying' || undefined}
              >
                {mode === 'applying' ? (
                  <>
                    <SaveSpinner size={12} />
                    {COPY.applying}
                  </>
                ) : (
                  <>
                    <Check className="w-3.5 h-3.5" aria-hidden />
                    {confirmButton}
                  </>
                )}
              </button>
              <button
                type="button"
                className={BTN_QUIET}
                disabled={mode === 'applying'}
                onClick={() => {
                  setMode('actions');
                  window.setTimeout(() => applyRef.current?.focus(), 0);
                }}
              >
                {COPY.cancel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProposalCard;
